import { Injectable } from '@angular/core';
import { CreditCardSummary } from '../../../models/commerce/cards-on-file/credit-card-summary';

@Injectable({
    providedIn: 'root',
})
export class CreditCardSummaryService {
    /**
     * Determines if the provided credit card is expired based on the current date and the card's expiration month and year.
     *
     * @param card The credit card summary to check for expiration.
     * @return `true` if the card is expired, otherwise `false`.
     */
    isExpired<T extends CreditCardSummary>(card: T): boolean {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        return (
            card.expirationYear < currentYear ||
            (card.expirationYear === currentYear &&
                card.expirationMonth < currentMonth)
        );
    }

    /**
     * Orders the provided list of credit cards for display, with the default card first, then by expiration
     * date (soonest to latest), then by last four digits of the card number.
     *
     * @param creditCards The list of credit cards to order.
     * @return The ordered list of credit cards.
     */
    orderCreditCards<T extends CreditCardSummary>(creditCards: T[]): T[] {
        return creditCards.sort((a, b) =>
            this.compareCreditCardsForDisplay(a, b),
        );
    }

    /**
     * Compares two credit cards for display order.
     *
     * @param a The first credit card to compare.
     * @param b The second credit card to compare.
     * @return A negative number if `a` should come before `b`, a positive number if `a` should come after `b`, or 0 if they are equal.
     */
    private compareCreditCardsForDisplay<T extends CreditCardSummary>(
        a: T,
        b: T,
    ): number {
        // Default card always comes first
        if (a.isDefault !== b.isDefault) {
            return a.isDefault ? -1 : 1;
        }

        // Sort by expiration year
        const yearDiff = a.expirationYear - b.expirationYear;
        if (yearDiff !== 0) {
            return yearDiff;
        }

        // Sort by expiration month
        const monthDiff = a.expirationMonth - b.expirationMonth;
        if (monthDiff !== 0) {
            return monthDiff;
        }

        // Sort by last four digits of the card number
        return a.truncatedCardNumber.localeCompare(b.truncatedCardNumber);
    }
}
